import React, { useState, useEffect, useCallback, Suspense, lazy } from "react";
import BarraMovil from "./components/BarraMovil";
import "./App.css";
import { supabase } from "./supabaseClient";
import ActualizacionCatalogo from "./components/ActualizacionCatalogo";
import AvisoIncidencias from "./components/AvisoIncidencias";
import AvisoSincronizacion from "./components/AvisoSincronizacion";
import { useSyncAS400 } from "./utils/syncAS400";
import { useModulo347 } from "./utils/modulo347";
import { setEmpresaActual } from "./utils/empresaActual";

// Carga perezosa: cada pantalla es un chunk aparte que solo se descarga
// cuando se visita, en vez de ir todas (Admin/Analytics con xlsx, SuperAdmin,
// PropietarioPanel...) dentro del bundle principal que carga todo el mundo
// antes de ver nada, incluyendo /registro y la landing pública.
const PortalCliente = lazy(() => import("./pages/PortalCliente"));
// El mismo panel que usa el propietario. Al supervisor le enseña solo sus
// centros y le esconde la vista global: eso lo decide el servidor, no aquí.
const InventarioPropietario = lazy(() => import("./pages/InventarioPropietario"));
const RegistroTrial = lazy(() => import("./pages/RegistroTrial"));

const Login = lazy(() => import("./pages/Login"));
const LoginCentro = lazy(() => import("./pages/LoginCentro"));
const LoginComercial = lazy(() => import("./pages/LoginComercial"));
const CentroPedidos = lazy(() => import("./pages/CentroPedidos"));
const CambiarPasswordCentro = lazy(() => import("./components/CambiarPasswordCentro"));
const CambiarPasswordRepartidor = lazy(() => import("./components/CambiarPasswordRepartidor"));
const LoginRepartidor = lazy(() => import("./pages/LoginRepartidor"));
const Delivery = lazy(() => import("./pages/Delivery"));
const Clientes = lazy(() => import("./pages/Clientes"));
const Pedido = lazy(() => import("./pages/Pedido"));
const MisPedidos = lazy(() => import("./pages/MisPedidos"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const Catalogo = lazy(() => import("./pages/Catalogo"));
const ClienteDetalle = lazy(() => import("./pages/ClienteDetalle"));
const Cobros = lazy(() => import("./pages/Cobros"));
const SuperAdmin = lazy(() => import("./pages/SuperAdmin"));
const PropietarioPanel = lazy(() => import("./pages/PropietarioPanel"));
const LeadsTrial = lazy(() => import("./pages/LeadsTrial"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Multiplicador = lazy(() => import("./pages/Multiplicador"));
const AccesoKorvantis = lazy(() => import("./pages/AccesoKorvantis"));
const LandingComerco = lazy(() => import("./pages/LandingComerco"));
const Formacion = lazy(() => import("./pages/Formacion"));
const MiPerfil = lazy(() => import("./pages/MiPerfil"));
const RutaMapa = lazy(() => import("./pages/RutaMapa"));
const RutaEquipo = lazy(() => import("./pages/RutaEquipo"));
const CambiarPassword = lazy(() => import("./pages/CambiarPassword"));
const PoliticaPrivacidad = lazy(() => import("./pages/legal/PoliticaPrivacidad"));
const LoginClienteFinal = lazy(() => import("./clientefinal/LoginClienteFinal"));
const TiendaClienteFinal = lazy(() => import("./clientefinal/TiendaClienteFinal"));
const CambiarPasswordClienteFinal = lazy(() => import("./clientefinal/CambiarPasswordClienteFinal"));
const LoginContenido = lazy(() => import("./pages/LoginContenido"));
const PanelContenido = lazy(() => import("./pages/PanelContenido"));
const InventarioTrabajador = lazy(() => import("./pages/InventarioTrabajador"));
const Modelo347 = lazy(() => import("./pages/Modelo347"));

const Cargando = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", color: "#64748B", fontSize: 14 }}>
    Cargando…
  </div>
);

function App() {
  const pathname = window.location.pathname;
  const esAccesoCentro = pathname === "/centro" || pathname === "/centros";
  const esAccesoDelivery = pathname === "/delivery";
  const esAccesoComercial = pathname === "/comercial" || pathname === "/comerciales" || pathname === "/app";
  const esRegistroTrial = pathname === "/registro";
  const esPoliticaPrivacidad = pathname === "/legal/privacidad";
  const esAccesoClienteFinal =
    pathname === "/profesionales" || pathname.startsWith("/profesionales/");
  const esLandingComerco = pathname === "/comerco";
  const esAccesoContenido = pathname === "/contenido";
  // Inventario anual: los trabajadores temporales entran por un enlace con
  // el id de la sesión (/inventario?s=…). No son usuarios de Korvantis, así
  // que esta ruta no comparte nada con el resto de sesiones.
  const esAccesoInventario = pathname === "/inventario";

  // F1 — Verificar expiración de la sesión del centro (máx 8 horas)
  const _centroRaw = localStorage.getItem("recordarCentro") === "true"
    ? JSON.parse(sessionStorage.getItem("centroActivo") || "null")
    : null;
  const centroGuardado = (_centroRaw && _centroRaw._expiraEn && Date.now() < _centroRaw._expiraEn)
    ? _centroRaw
    : null;
  if (!centroGuardado && _centroRaw) {
    // Sesión expirada → limpiar
    sessionStorage.removeItem("centroActivo");
    localStorage.removeItem("recordarCentro");
  }

  const [centroActivo, setCentroActivo] = useState(centroGuardado);

  // Sesión del repartidor — aislada, mismo patrón de expiración que centroActivo
  const _repartidorRaw = localStorage.getItem("recordarRepartidor") === "true"
    ? JSON.parse(sessionStorage.getItem("repartidorActivo") || "null")
    : null;
  const repartidorGuardado = (_repartidorRaw && _repartidorRaw._expiraEn && Date.now() < _repartidorRaw._expiraEn)
    ? _repartidorRaw
    : null;
  if (!repartidorGuardado && _repartidorRaw) {
    sessionStorage.removeItem("repartidorActivo");
    localStorage.removeItem("recordarRepartidor");
  }

  const [repartidorActivo, setRepartidorActivo] = useState(repartidorGuardado);

  // Sesión del cliente final (bar/negocio) — aislada, no comparte estado con comercial/centro
  const _clienteFinalRaw = JSON.parse(sessionStorage.getItem("clienteFinalActivo") || "null");
  const clienteFinalGuardado = (_clienteFinalRaw && _clienteFinalRaw._expiraEn && Date.now() < _clienteFinalRaw._expiraEn)
    ? _clienteFinalRaw
    : null;
  if (!clienteFinalGuardado && _clienteFinalRaw) {
    sessionStorage.removeItem("clienteFinalActivo");
    localStorage.removeItem("recordarClienteFinal");
  }
  const [clienteFinalActivo, setClienteFinalActivo] = useState(clienteFinalGuardado);

  // Sesión del gestor de contenido (marketing) — igual de aislada, tabla
  // propia `gestores_contenido`, sin relación con `agentes`.
  const gestorContenidoGuardado = JSON.parse(sessionStorage.getItem("gestorContenidoActivo") || "null");
  const [gestorContenidoActivo, setGestorContenidoActivo] = useState(gestorContenidoGuardado);

  const usuarioGuardado =
    JSON.parse(sessionStorage.getItem("usuarioActivo")) || null;

  const [usuario, setUsuario] = useState(usuarioGuardado);
  // Sin sincronizar con el AS/400 > 26 h: pedidos bloqueados (fichas y cobros siguen)
  const syncAS400 = useSyncAS400(usuario);
  const [menuMovil, setMenuMovil] = useState(false);   // cajón del menú en el móvil
  const [sessionLista, setSessionLista] = useState(false);
  const [moduloAnalytics, setModuloAnalytics] = useState(false);
  // Multiplicador: lo activa el superadmin por empresa; el jefe ve la subida
  const [moduloMultiplicador, setModuloMultiplicador] = useState(false);
  const [moduloFormacion, setModuloFormacion] = useState(false);
  const [moduloInventario, setModuloInventario] = useState(false);
  // Botón «Mod. 347»: lo enciende el jefe de comerciales (enero–marzo)
  const [modulo347] = useModulo347(usuario);

  useEffect(() => {
    const empresaId = usuario?.empresa_id;
    if (usuario?.rol !== "admin" || !empresaId) {
      setModuloAnalytics(false);
      return;
    }
    supabase.from("empresas").select("modulo_analytics").eq("id", empresaId).single()
      .then(({ data }) => setModuloAnalytics(!!data?.modulo_analytics));
  }, [usuario]);

  useEffect(() => {
    const empresaId = usuario?.empresa_id;
    if (usuario?.rol !== "admin" || !empresaId) {
      setModuloMultiplicador(false);
      return;
    }
    supabase.from("empresas").select("modulo_multiplicador").eq("id", empresaId).single()
      .then(({ data }) => setModuloMultiplicador(data?.modulo_multiplicador === true));
  }, [usuario]);

  // Nombre, logo y ERP de la empresa que entra: ninguna pantalla los lleva fijos
  useEffect(() => {
    const empresaId = usuario?.empresa_id;
    if (!empresaId || usuario?.rol === "superadmin") return;
    supabase.from("empresas").select("nombre, logo_url, usa_as400, erp_nombre").eq("id", empresaId).single()
      .then(({ data }) => { if (data) setEmpresaActual(data); });
  }, [usuario]);

  // El inventario lo lleva el administrador como supervisor de sus centros.
  useEffect(() => {
    const empresaId = usuario?.empresa_id;
    if (!["admin", "supervisor"].includes(usuario?.rol) || !empresaId) {
      setModuloInventario(false);
      return;
    }
    supabase.from("empresas").select("modulo_inventario").eq("id", empresaId).single()
      .then(({ data }) => setModuloInventario(!!data?.modulo_inventario));
  }, [usuario]);

  // La formación la usan comerciales y admin, no solo admin como Analytics:
  // el jefe también hace la ruta, y además necesita ver la pestaña del panel.
  useEffect(() => {
    const empresaId = usuario?.empresa_id;
    if (!empresaId || usuario?.rol === "superadmin") {
      setModuloFormacion(false);
      return;
    }
    supabase.from("empresas").select("modulo_formacion").eq("id", empresaId).single()
      .then(({ data }) => setModuloFormacion(!!data?.modulo_formacion));
  }, [usuario]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        // Verificar rol real contra la BD (nunca confiar solo en sessionStorage)
        const { data: agenteReal } = await supabase
          .from("agentes")
          .select("rol, empresa_id, activo")
          .eq("auth_user_id", session.user.id)
          .single();

        if (agenteReal && usuarioGuardado) {
          // Si el rol almacenado no coincide con el rol real, o el agente está desactivado → cerrar sesión
          if (agenteReal.rol !== usuarioGuardado.rol || agenteReal.activo === false) {
            sessionStorage.removeItem("usuarioActivo");
            await supabase.auth.signOut();
            setUsuario(null);
          }
        }
      } else if (usuarioGuardado && !centroGuardado) {
        // No hay sesión JWT activa pero hay un usuario comercial en sessionStorage
        // → fue autenticado por el flujo legacy (texto plano). Forzar logout.
        sessionStorage.removeItem("usuarioActivo");
        setUsuario(null);
      }
      setSessionLista(true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [pagina, setPagina] = useState(
    usuarioGuardado?.rol === "superadmin"
      ? "superadmin"
      : usuarioGuardado?.rol === "admin"
      ? "admin"
      : usuarioGuardado?.rol === "propietario"
      ? "propietario"
      // El supervisor de inventario no tiene panel de ventas: al recargar
      // la página vuelve a lo suyo, no a un dashboard vacío que no le
      // corresponde.
      : usuarioGuardado?.rol === "supervisor"
      ? "inventario"
      : "dashboard"
  );

  // Red de seguridad: da igual por dónde haya entrado o qué hubiera
  // guardado la sesión anterior, un supervisor de inventario nunca se queda
  // en una pantalla de ventas. Es su única pestaña, así que si acaba en
  // otra parte es un error, no una elección suya.
  useEffect(() => {
    if (usuario?.rol === "supervisor" && !["inventario", "perfil"].includes(pagina)) {
      setPagina("inventario");
    }
  }, [usuario, pagina]);

  const salir = useCallback(() => {
    sessionStorage.removeItem("usuarioActivo");
    sessionStorage.removeItem("clienteSeleccionado");
    sessionStorage.removeItem("pedidoDuplicado");
    sessionStorage.removeItem("abrirPedidoDuplicado");
    supabase.auth.signOut();
    setUsuario(null);
    setPagina("dashboard");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!usuario) return;
    const TIMEOUT = 30 * 60 * 1000;
    let timer = setTimeout(salir, TIMEOUT);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(salir, TIMEOUT);
    };
    window.addEventListener("mousemove", reset);
    window.addEventListener("keypress", reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", reset);
      window.removeEventListener("keypress", reset);
    };
  }, [usuario, salir]);

  const textoTrial = (usuarioActual) => {
    if (usuarioActual?.plan !== "trial" || !usuarioActual?.trial_fin) {
      return null;
    }

    const ahora = new Date();
    const fin = new Date(usuarioActual.trial_fin);
    const diferencia = fin - ahora;

    if (diferencia <= 0) return "Plan gratuito finalizado";

    const dias = Math.ceil(diferencia / (1000 * 60 * 60 * 24));

    return `Plan gratuito · quedan ${dias} día${dias === 1 ? "" : "s"}`;
  };

  if (!sessionLista) return null;

  if (pathname === "/" && !usuarioGuardado) {
    return (
      <Suspense fallback={<Cargando />}>
        <AccesoKorvantis />
      </Suspense>
    );
  }

  if (esLandingComerco) {
    window.location.replace("/profesionales");
    return null;
  }

  if (esAccesoComercial) {
    if (!usuario) {
      return (
        <Suspense fallback={<Cargando />}>
          <LoginComercial
            setUsuario={(u) => {
              setUsuario(u);
              if (u?.rol === "superadmin") setPagina("superadmin");
              else if (u?.rol === "admin") setPagina("admin");
              else if (u?.rol === "propietario") setPagina("propietario");
              else setPagina("dashboard");
            }}
          />
        </Suspense>
      );
    }
  }

  if (esAccesoClienteFinal) {
    if (!clienteFinalActivo) {
      return (
        <Suspense fallback={<Cargando />}>
          <LoginClienteFinal setClienteFinalActivo={setClienteFinalActivo} />
        </Suspense>
      );
    }

    // Acceso activado desde el perfil de Centro con contraseña de un solo
    // uso (ver migration/42 + centro-clientes): bloquea el resto de la app
    // hasta que el cliente establezca su propia contraseña.
    if (clienteFinalActivo.password_temporal) {
      return (
        <Suspense fallback={<Cargando />}>
          <CambiarPasswordClienteFinal
            clienteFinalActivo={clienteFinalActivo}
            setClienteFinalActivo={setClienteFinalActivo}
          />
        </Suspense>
      );
    }

    return (
      <Suspense fallback={<Cargando />}>
        <TiendaClienteFinal
          clienteFinalActivo={clienteFinalActivo}
          salir={() => {
            sessionStorage.removeItem("clienteFinalActivo");
            localStorage.removeItem("recordarClienteFinal");
            // Eliminar cookie de sesión de middleware
            document.cookie = "korvantis_session=; SameSite=Strict; Secure; Max-Age=0; Path=/";
            setClienteFinalActivo(null);
          }}
        />
      </Suspense>
    );
  }

  if (esAccesoContenido) {
    if (!gestorContenidoActivo) {
      return (
        <Suspense fallback={<Cargando />}>
          <LoginContenido setGestorActivo={setGestorContenidoActivo} />
        </Suspense>
      );
    }

    return (
      <Suspense fallback={<Cargando />}>
        <PanelContenido
          gestorActivo={gestorContenidoActivo}
          salir={() => {
            sessionStorage.removeItem("gestorContenidoActivo");
            supabase.auth.signOut();
            setGestorContenidoActivo(null);
          }}
        />
      </Suspense>
    );
  }

  if (esAccesoInventario) {
    return (
      <Suspense fallback={<Cargando />}>
        <InventarioTrabajador />
      </Suspense>
    );
  }

  if (esAccesoCentro) {
    if (!centroActivo) {
      return (
        <Suspense fallback={<Cargando />}>
          <LoginCentro setCentroActivo={setCentroActivo} />
        </Suspense>
      );
    }

    if (centroActivo.password_temporal) {
      const salirCentro = () => {
        sessionStorage.removeItem("centroActivo");
        localStorage.removeItem("centroActivo");
        localStorage.removeItem("recordarCentro");
        setCentroActivo(null);
      };
      return (
        <Suspense fallback={<Cargando />}>
          <CambiarPasswordCentro
            centro={centroActivo}
            obligatorio
            onCancelar={salirCentro}
            onHecho={() => {
              const actualizado = { ...centroActivo, password_temporal: false };
              try {
                if (sessionStorage.getItem("centroActivo")) sessionStorage.setItem("centroActivo", JSON.stringify(actualizado));
              } catch {}
              setCentroActivo(actualizado);
            }}
          />
        </Suspense>
      );
    }

    return (
      <Suspense fallback={<Cargando />}>
        <CentroPedidos
          centroActivo={centroActivo}
          setCentroActivo={setCentroActivo}
        />
      </Suspense>
    );
  }

  if (esAccesoDelivery) {
    if (!repartidorActivo) {
      return (
        <Suspense fallback={<Cargando />}>
          <LoginRepartidor setRepartidorActivo={setRepartidorActivo} />
        </Suspense>
      );
    }

    // Contraseña de un solo uso puesta por el centro: tiene que poner la suya
    if (repartidorActivo.password_temporal) {
      const salirRepartidor = () => {
        localStorage.removeItem("repartidorToken");
        localStorage.removeItem("recordarRepartidor");
        sessionStorage.removeItem("repartidorActivo");
        setRepartidorActivo(null);
      };
      return (
        <Suspense fallback={<Cargando />}>
          <CambiarPasswordRepartidor
            repartidor={repartidorActivo}
            obligatorio
            onCancelar={salirRepartidor}
            onHecho={() => {
              const actualizado = { ...repartidorActivo, password_temporal: false };
              try {
                if (sessionStorage.getItem("repartidorActivo")) sessionStorage.setItem("repartidorActivo", JSON.stringify(actualizado));
              } catch {}
              setRepartidorActivo(actualizado);
            }}
          />
        </Suspense>
      );
    }

    return (
      <Suspense fallback={<Cargando />}>
        <Delivery
          repartidorActivo={repartidorActivo}
          setRepartidorActivo={setRepartidorActivo}
        />
      </Suspense>
    );
  }

  if (esPoliticaPrivacidad) {
    return (
      <Suspense fallback={<Cargando />}>
        <PoliticaPrivacidad />
      </Suspense>
    );
  }

  if (esRegistroTrial) {
    return (
      <Suspense fallback={<Cargando />}>
        <RegistroTrial setUsuario={setUsuario} />
      </Suspense>
    );
  }

  if (!usuario) {
    return (
      <Suspense fallback={<Cargando />}>
        <Login
          setUsuario={(u) => {
            setUsuario(u);

            if (u?.rol === "superadmin") {
              setPagina("superadmin");
            } else if (u?.rol === "admin") {
              setPagina("admin");
            } else if (u?.rol === "propietario") {
              setPagina("propietario");
            } else if (u?.rol === "supervisor") {
              setPagina("inventario");
            } else {
              setPagina("dashboard");
            }
          }}
        />
      </Suspense>
    );
  }

  if (
    window.location.pathname === "/superadmin" &&
    usuario?.rol !== "superadmin"
  ) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Acceso no autorizado</h1>
        <p>No tienes permisos para entrar en SuperAdmin.</p>

        <button
          onClick={() => {
            sessionStorage.removeItem("usuarioActivo");
            window.location.href = "/";
          }}
        >
          Volver al login
        </button>
      </div>
    );
  }

  // Herramientas que viven fuera de Korvantis pero son parte del trabajo
  // diario del supervisor. Se abren en otra pestaña para no perder lo que
  // tenga a medias aquí dentro.
  const botonEnlace = (icono, texto, url) => (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="menu-btn"
      style={{ textDecoration: "none" }}
    >
      <span className="menu-icon">{icono}</span>
      <span>{texto}</span>
    </a>
  );

  // Botón de temporada, de otro color para que se vea
  const boton347 = () => (
    <button
      onClick={() => setPagina("m347")}
      className={pagina === "m347" ? "menu-btn menu-btn-347 active" : "menu-btn menu-btn-347"}
    >
      <span className="menu-icon">🧾</span>
      <span>Mod. 347</span>
    </button>
  );

  const botonMenu = (icono, texto, valor) => (
    <button
      onClick={() => setPagina(valor)}
      className={pagina === valor ? "menu-btn active" : "menu-btn"}
    >
      <span className="menu-icon">{icono}</span>
      <span>{texto}</span>
    </button>
  );

  // Primer acceso con la contraseña que le puso quien creó el usuario:
  // no entra hasta que pone la suya.
  if (usuario && usuario.rol !== "superadmin" && usuario.password_temporal) {
    return (
      <Suspense fallback={<Cargando />}>
        <CambiarPassword usuario={usuario} setUsuario={setUsuario} />
      </Suspense>
    );
  }

  if (usuario?.rol === "propietario") {
    return (
      <Suspense fallback={<Cargando />}>
        <PropietarioPanel usuario={usuario} salir={salir} />
      </Suspense>
    );
  }

  if (pagina === "pedido") {
    if (usuario?.rol === "admin" || usuario?.rol === "superadmin") {
      setPagina(usuario?.rol === "superadmin" ? "superadmin" : "admin");
      return null;
    }

    if (syncAS400.bloqueado) {
      return (
        <div style={{ minHeight: "100vh", background: "#F4F7FB", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 18, padding: 24, maxWidth: 440, textAlign: "center", boxShadow: "0 12px 40px rgba(0,0,0,0.12)" }}>
            <div style={{ fontSize: 40 }}>⚠️</div>
            <h2 style={{ margin: "6px 0 10px", color: "#B42318" }}>Pedidos no disponibles</h2>
            <p style={{ fontSize: 15, lineHeight: 1.5, color: "#243B53" }}>
              Korvantis está sin conexión con el sistema y los precios y el stock podrían no estar al día.
              Mientras dure la incidencia no se pueden hacer ni enviar pedidos. Disculpa las molestias:
              ya estamos trabajando para solucionarlo.
            </p>
            <button onClick={() => setPagina("clientes")} style={{ marginTop: 8, background: "#102A43", color: "white", border: "none", borderRadius: 12, padding: "12px 22px", fontSize: 15, fontWeight: 700, cursor: "pointer", width: "100%" }}>
              Volver a clientes
            </button>
          </div>
        </div>
      );
    }

    return (
      <Suspense fallback={<Cargando />}>
        <Pedido setPagina={setPagina} />
      </Suspense>
    );
  }

  return (
    <div className={menuMovil ? "app-shell menu-abierto" : "app-shell"}>
      <BarraMovil
        items={
          usuario?.rol === "admin"
            ? [{ icono: "📊", texto: "Dashboard", valor: "dashboard" },
               { icono: "👥", texto: "Clientes", valor: "clientes" },
               { icono: "📋", texto: "Pedidos", valor: "misPedidos" }]
            : usuario?.rol === "superadmin"
            ? [{ icono: "🏢", texto: "Empresas", valor: "superadmin" },
               { icono: "🎯", texto: "Solicitudes", valor: "leadsTrial" }]
            : usuario?.rol === "supervisor"
            ? [{ icono: "📦", texto: "Inventario", valor: "inventario" }]
            : [{ icono: "📊", texto: "Mis ventas", valor: "dashboard" },
               { icono: "👥", texto: "Clientes", valor: "clientes" },
               { icono: "📋", texto: "Pedidos", valor: "misPedidos" }]
        }
        activo={pagina}
        onElegir={setPagina}
        abierto={menuMovil}
        setAbierto={setMenuMovil}
      />
      <aside
        onClick={(e) => { if (e.target.closest("button, a")) setMenuMovil(false); }}
        className="sidebar"
        style={{
          background: "linear-gradient(180deg, #102A43 0%, #0A84FF 100%)",
        }}
      >
        <div className="brand-box">
          <div className="brand-row">
            <img src="/logo.png" alt="KORVANTIS" className="brand-logo" />
            <div style={{ minWidth: 0, textAlign: "left" }}>
              <div className="brand-nombre">KORVANTIS</div>
              <div className="brand-sub">Sales Management</div>
            </div>
          </div>

          {usuario?.nombreEmpresa && <div className="brand-empresa">{usuario.nombreEmpresa}</div>}

          {usuario && textoTrial(usuario) && (
            <p
              style={{
                marginTop: 4,
                fontSize: 11,
                color:
                  textoTrial(usuario).includes("1 día") ||
                  textoTrial(usuario).includes("finalizado")
                    ? "#FFD166"
                    : "#d7e7ff",
                fontWeight: "700",
                textAlign: "center",
                opacity: 0.95,
              }}
            >
              ⏳ {textoTrial(usuario)}
            </p>
          )}

          <div className="brand-usuario" title={usuario?.usuario || ""}>
            👤 {usuario?.nombre || usuario?.usuario || "—"}
            <span className="brand-rol">
              {{ superadmin: "Super Administrador", propietario: "Administrador", admin: "Jefe de comerciales",
                 agente: "Comercial", supervisor: "Supervisor" }[usuario?.rol] || usuario?.rol || ""}
            </span>
          </div>
        </div>

        <div className="menu">
          {usuario?.rol === "superadmin" && (
            <>
              {botonMenu("🏢", "Empresas", "superadmin")}
              {botonMenu("🎯", "Solicitudes prueba", "leadsTrial")}
            </>
          )}

          {/* El supervisor de inventario no es un comercial ni un jefe de
              comerciales: no tiene por qué ver clientes ni pedidos. Su
              menú es una sola cosa. */}
          {usuario?.rol === "supervisor" && (
            <>
              {botonMenu("📦", "Inventario", "inventario")}
              {botonEnlace("🧾", "Facturas pendientes", "https://facturas-pendientes.nebogroup.io")}
              {botonEnlace("📄", "PDF a Excel", "https://excel.nebogroup.io")}
            </>
          )}

          {usuario?.rol === "admin" && (
            <>
              {botonMenu("⚙️", "Administración", "admin")}
              {botonMenu("📊", "Dashboard", "dashboard")}
              {botonMenu("👥", "Clientes", "clientes")}
              {botonMenu("🗺️", "Ruta", "ruta")}
              {botonMenu("📋", "Pedidos", "misPedidos")}
              {botonMenu("💶", "Cobros", "cobros")}
              {modulo347 && boton347()}
              {moduloAnalytics && botonMenu("📈", "Analytics", "analytics")}
              {moduloMultiplicador && botonMenu("✖️", "Multiplicador", "multiplicador")}
              {moduloInventario && botonMenu("📦", "Inventario", "inventario")}
              {moduloFormacion && botonMenu("🧭", "La Ruta", "formacion")}
            </>
          )}

          {usuario?.rol !== "admin" &&
            usuario?.rol !== "superadmin" &&
            usuario?.rol !== "supervisor" && (
              <>
                {botonMenu("📊", "Mis ventas", "dashboard")}
                {botonMenu("👥", "Clientes", "clientes")}
                {botonMenu("🗺️", "Ruta", "ruta")}
                {botonMenu("📋", "Mis pedidos", "misPedidos")}
                {botonMenu("💶", "Mis cobros", "cobros")}
                {modulo347 && boton347()}
                {moduloFormacion && botonMenu("🧭", "La Ruta", "formacion")}
              </>
            )}
        </div>

        <div className="menu">
          {botonMenu("👤", "Mi perfil", "perfil")}
        </div>

        <button onClick={salir} className="logout-btn">
          Salir
        </button>

        <p style={{ fontSize: 10, color: "#7a9cc0", textAlign: "center", padding: "0 12px 12px", margin: 0, lineHeight: 1.4 }}>
          Versión {process.env.REACT_APP_VERSION || "—"} · © 2026 Korvantis
        </p>
      </aside>

      <ActualizacionCatalogo onActualizado={async () => {}} />

      {usuario?.rol !== "admin" && usuario?.rol !== "superadmin" && usuario?.rol !== "supervisor" && (
        <AvisoIncidencias
          usuario={usuario}
          setPagina={setPagina}
          paginaActual={pagina}
        />
      )}

      <main className="main-area" style={{ background: "#F4F7FB" }}>
        <AvisoSincronizacion sync={syncAS400} />
        <Suspense fallback={<Cargando />}>
          {pagina === "superadmin" &&
            usuario?.rol === "superadmin" && <SuperAdmin />}

          {pagina === "leadsTrial" &&
            usuario?.rol === "superadmin" && <LeadsTrial />}

          {pagina === "admin" && usuario?.rol === "admin" && <Admin />}

          {pagina === "inventario" &&
            ["admin", "supervisor"].includes(usuario?.rol) &&
            moduloInventario && <InventarioPropietario />}


          {pagina === "portalCliente" &&
            usuario?.rol === "admin" && <PortalCliente />}

          {pagina === "dashboard" &&
            usuario?.rol !== "superadmin" && <Dashboard />}

          {pagina === "clientes" &&
            usuario?.rol !== "superadmin" && (
              <Clientes setPagina={setPagina} />
            )}

          {pagina === "clienteDetalle" &&
            usuario?.rol !== "superadmin" && (
              <ClienteDetalle setPagina={setPagina} />
            )}

          {pagina === "cobros" &&
            !["superadmin", "supervisor"].includes(usuario?.rol) && <Cobros />}

          {pagina === "m347" &&
            modulo347 &&
            !["superadmin", "supervisor"].includes(usuario?.rol) && <Modelo347 />}

          {pagina === "catalogo" &&
            usuario?.rol !== "admin" && <Catalogo />}

          {pagina === "misPedidos" &&
            usuario?.rol !== "superadmin" && (
              <MisPedidos setPagina={setPagina} />
            )}

          {pagina === "multiplicador" &&
            usuario?.rol === "admin" &&
            moduloMultiplicador && <Multiplicador usuario={usuario} />}

          {pagina === "analytics" &&
            usuario?.rol === "admin" &&
            moduloAnalytics && (
              <Analytics setPagina={setPagina} />
            )}

          {pagina === "ruta" &&
            !["admin", "superadmin", "supervisor"].includes(usuario?.rol) && <RutaMapa />}

          {pagina === "ruta" && usuario?.rol === "admin" && <RutaEquipo />}

          {pagina === "perfil" && (
            <MiPerfil usuario={usuario} setUsuario={setUsuario} />
          )}

          {pagina === "formacion" &&
            usuario?.rol !== "superadmin" &&
            moduloFormacion && <Formacion />}
        </Suspense>
      </main>
    </div>
  );
}

export default App;
